import { useCallback, useMemo, useState } from 'react';

import type { TranslationKey } from '@change-corgi/core/i18n';
import { useI18n } from '@change-corgi/core/react/i18n';
import { useNavigate } from '@change-corgi/core/react/router';
import { useTracking } from '@change-corgi/core/react/tracking';

import { usePsfPromoteOrSharePageData } from 'src/app/pages/psf/promoteOrShare/pageContext';
import type { ShareChannel } from 'src/app/pages/psf/promoteOrShare/shared/types';
import type { ShareAction } from 'src/app/shared/hooks/share';

import { usePromoteOrShareActionUrls } from '../usePromoteOrShareActionUrls';

import { mapButtonConfigs } from './mapButtonConfigs';
import { usePsfShareAvailableShareActions } from './share';

type UseActionItemsResult = ModelHookResult<
	{
		buttonConfigs: ReadonlyArray<{
			ShareAction: ShareAction<ShareChannel>['ShareAction'];
			channel: ShareChannel;
			label: TranslationKey;
			successLabel?: TranslationKey;
			secondaryLabel?: TranslationKey;
			url: string;
			shortUrl?: string;
		}>;
		continueButtonProps: {
			label: string;
			action: () => void;
		};
		shareVisualUrl: string | undefined;
	},
	{
		handleOpenShareSlideUp: () => void;
		handleCloseShareSlideUp: () => void;
	}
>;

export function useShareActions(): UseActionItemsResult {
	const [shortLinksRequired, setShortLinksRequired] = useState(false);
	const availableShareActions = usePsfShareAvailableShareActions({ disableShortLinks: !shortLinksRequired });
	const {
		data: { petition, isShareAssetsEnabled },
	} = usePsfPromoteOrSharePageData();

	const shareVisualUrl = isShareAssetsEnabled ? petition.bestShareVisual?.url : undefined;

	return {
		data: {
			buttonConfigs: useMemo(() => mapButtonConfigs(availableShareActions), [availableShareActions]),
			continueButtonProps: useContinueButtonProps(),
			shareVisualUrl,
		},
		actions: useHandlers(setShortLinksRequired),
	};
}

function useHandlers(setShortLinksRequired: (value: boolean) => void) {
	const track = useTracking();

	return {
		handleOpenShareSlideUp: useCallback(() => {
			setShortLinksRequired(true);
			void track('combo_psf_share');
			void track('combo_psf_share_slide_up_opened');
		}, [track, setShortLinksRequired]),
		handleCloseShareSlideUp: useCallback(() => {
			void track('combo_psf_share_slide_up_closed');
		}, [track]),
	};
}

function useContinueButtonProps() {
	const track = useTracking();
	const { translate } = useI18n();
	const navigate = useNavigate();
	const {
		data: { shareButtonUrl: continueUrl },
	} = usePromoteOrShareActionUrls();

	return {
		label: translate('corgi.psf.share.footer.continue'),
		action: useCallback(() => {
			void track('combo_psf_share_slide_up_continue');
			navigate(continueUrl);
		}, [continueUrl, navigate, track]),
	};
}
